import {
  Youtube,
  Rss,
  Music4,
  Type,
  Settings,
  Music2,
  Instagram,
  Megaphone,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { ComingSoon } from '@/v2/components/ComingSoon';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

const sources = [
  { icon: Youtube, label: 'YouTube', color: 'text-red-500' },
  { icon: Rss, label: i18n.t('automations.sourceBlogRss', { ns: 'v2' }), color: 'text-orange-500' },
  { icon: Music4, label: i18n.t('automations.sourceMusic', { ns: 'v2' }), color: 'text-primary' },
  { icon: Type, label: i18n.t('automations.sourcePrompt', { ns: 'v2' }), color: 'text-text-primary' },
];

const destinations = [
  { icon: Music2, label: 'TikToks', color: 'text-text-primary' },
  { icon: Youtube, label: 'Shorts', color: 'text-red-500' },
  { icon: Instagram, label: 'Reels', color: 'text-pink-500' },
];

const automations = [
  {
    icon: Megaphone,
    iconColor: 'text-orange-400',
    title: i18n.t('automations.productAutoModeTitle', { ns: 'v2' }),
    desc: i18n.t('automations.productAutoModeDesc', { ns: 'v2' }),
  },
  {
    icon: Youtube,
    iconColor: 'text-red-500',
    title: i18n.t('automations.youtubeToShortsTitle', { ns: 'v2' }),
    desc: i18n.t('automations.youtubeToShortsDesc', { ns: 'v2' }),
  },
  {
    icon: Rss,
    iconColor: 'text-orange-500',
    title: i18n.t('automations.blogToVideoTitle', { ns: 'v2' }),
    desc: i18n.t('automations.blogToVideoDesc', { ns: 'v2' }),
  },
  {
    icon: Music4,
    iconColor: 'text-primary',
    title: i18n.t('automations.dailyKidsMusicTitle', { ns: 'v2' }),
    desc: i18n.t('automations.dailyKidsMusicDesc', { ns: 'v2' }),
  },
];

// Connector between the diagram groups: a flexible dashed line on desktop; a small
// down-arrow on mobile, where the groups stack vertically instead of sitting in a row.
function Connector() {
  return (
    <>
      <ArrowRight className="sm:hidden w-5 h-5 text-text-tertiary rotate-90 shrink-0" />
      <div className="hidden sm:block flex-1 self-center border-t-2 border-dashed border-glass-border relative">
        <ArrowRight className="absolute -right-1 -top-2.5 w-5 h-5 text-text-tertiary" />
      </div>
    </>
  );
}

export function AutomationsPage() {
  const { t } = useTranslation('v2');
  return (
    <ComingSoon note={t('automations.comingSoonNote')}>

    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
      <div className="flex items-center gap-3 mb-10">
        <h1 className="text-3xl font-extrabold text-text-primary">{t('automations.title')}</h1>
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary text-[10px] font-bold uppercase tracking-wide">{t('automations.comingSoonBadge')}</span>
      </div>

      {/* Workflow diagram — a horizontal row on desktop; stacks vertically on mobile
          so the worker card + "Vivideo" badge never get clipped off a narrow screen. */}
      <div className="glass rounded-3xl p-5 sm:p-10 mb-12">
        <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-6">
          {/* Sources */}
          <div className="flex flex-col gap-4">
            {sources.map((source) => (
              <div key={source.label} className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl bg-surface-light flex items-center justify-center shadow-lg">
                  <source.icon className={cn('w-5 h-5', source.color)} />
                </span>
                <span className="text-xs text-text-tertiary">{source.label}</span>
              </div>
            ))}
          </div>

          <Connector />

          {/* Worker */}
          <div className="relative">
            <span className="absolute -top-3 right-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-primary to-accent text-[10px] font-bold text-white uppercase">
              Vivideo
            </span>
            <div className="w-44 rounded-2xl bg-surface-light border border-primary/30 shadow-glow-sm p-6 flex flex-col items-center gap-3">
              <Settings className="w-8 h-8 text-primary animate-[spin_6s_linear_infinite]" />
              <div className="text-center">
                <p className="font-bold text-text-primary">vivideo</p>
                <p className="text-xs text-text-tertiary">{t('automations.worker')}</p>
              </div>
            </div>
          </div>

          <Connector />

          {/* Destinations */}
          <div className="flex flex-col gap-4">
            {destinations.map((dest) => (
              <div key={dest.label} className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl bg-surface-light flex items-center justify-center shadow-lg">
                  <dest.icon className={cn('w-5 h-5', dest.color)} />
                </span>
                <span className="text-xs text-text-tertiary">{dest.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tagline */}
      <div className="text-center mb-12">
        <h2 className="text-2xl font-bold text-text-primary mb-2">{t('automations.taglineHeading')}</h2>
        <p className="text-sm text-text-secondary max-w-md mx-auto">
          {t('automations.taglineBody')}
        </p>
      </div>

      {/* Automation cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {automations.map((automation) => (
          <div
            key={automation.title}
            className="glass rounded-2xl p-6 text-center hover:bg-surface-light/50 transition-colors cursor-pointer"
          >
            <span className="inline-flex w-12 h-12 rounded-full bg-surface-light items-center justify-center mb-4">
              <automation.icon className={cn('w-5 h-5', automation.iconColor)} />
            </span>
            <h3 className="font-bold text-text-primary mb-2">{automation.title}</h3>
            <p className="text-xs text-text-tertiary leading-relaxed">{automation.desc}</p>
          </div>
        ))}
      </div>
    </div>
    </ComingSoon>
  );
}
